/**
 * Subscription Picker Web Component
 * Handles subscription plan selection and form updates
 */

class SubscriptionPicker extends HTMLElement {
  constructor() {
    super();
    this.formId = this.getAttribute('form');
  }

  connectedCallback() {
    this.setupEventListeners();
    this.setupVariantChangeListener();
  }

  disconnectedCallback() {
    // Clean up the variant change listener
    if (this.boundVariantChangeHandler) {
      document.removeEventListener('variant:change', this.boundVariantChangeHandler);
    }
  }

  /**
   * Set up event listeners for subscription group elements
   */
  setupEventListeners() {
    // Find all subscription groups within this picker
    const subscriptionGroups = this.querySelectorAll('subscription-group');
    
    subscriptionGroups.forEach(group => {
      const selector = group.querySelector('[data-id="selling-plan-selector"]');
      
      if (selector) {
        // Listen for changes to the selling plan selector
        selector.addEventListener('change', (event) => {
          this.handleSellingPlanChange(group, event.target.value);
        });
      }
    });

    // Listen for radio button changes to update active subscription group
    const radioButtons = this.querySelectorAll('input[type="radio"][name="properties[Purchase Type]"]');
    radioButtons.forEach(radio => {
      radio.addEventListener('change', (event) => {
        if (event.target.checked) {
          this.handlePurchaseTypeChange(event.target);
        }
      });
    });
  }

  /**
   * Set up listener for variant change events
   */
  setupVariantChangeListener() {
    if (this.formId) {
      const productForm = document.getElementById(this.formId);
      if (productForm) {
        productForm.addEventListener('variant:change', (event) => {
          this.onVariantChangedListener(event);
        });
      }
    }
  }

  /**
   * Handle variant change and update subscription prices
   * @param {Object} event - The variant change event data
   */
  onVariantChangedListener(event) {
    // Get all subscription groups within this picker
    const subscriptionGroups = this.querySelectorAll('subscription-group');
    
    subscriptionGroups.forEach(group => {
      // Get the currently selected option for this group
      const selector = group.querySelector('[data-id="selling-plan-selector"]');
      
      if (selector) {
        const selectedOption = selector.querySelector(`option[value="${selector.value}"]`);

        if (selectedOption) {
          // Update the price for this subscription group
          this.updateSubscriptionPricing(group, selectedOption);
        }
      }
    });
  }

  /**
   * Handle selling plan dropdown change
   * @param {HTMLElement} subscriptionGroup - The subscription group element
   * @param {string} sellingPlanId - The selected selling plan ID
   */
  handleSellingPlanChange(subscriptionGroup, sellingPlanId) {
    // Step 1: Update the selling-plan-id attribute on the subscription-group element
    subscriptionGroup.setAttribute('selling-plan-id', sellingPlanId);

    // Step 2: Extract and set the frequency amount from the selected option's data attribute
    const selector = subscriptionGroup.querySelector('[data-id="selling-plan-selector"]');
    if (selector) {
      const selectedOption = selector.querySelector(`option[value="${sellingPlanId}"]`);
      if (selectedOption) {
        const frequencyAmount = selectedOption.getAttribute('data-frequency-amount');
        if (frequencyAmount) {
          subscriptionGroup.setAttribute('data-frequency-amount', frequencyAmount);
        }
        
        // Step 3: Update pricing based on the selected plan
        this.updateSubscriptionPricing(subscriptionGroup, selectedOption);
      }
    }

    // Step 4: Find and update the form's hidden selling_plan input
    this.updateFormSellingPlan(sellingPlanId);
  }

  /**
   * Handle purchase type radio button change
   * @param {HTMLElement} radioButton - The selected radio button
   */
  handlePurchaseTypeChange(radioButton) {
    // Hide all subscription frequency options first
    const allFrequencyOptions = this.querySelectorAll('.subscriptions__frequency-options');
    allFrequencyOptions.forEach(option => {
      option.setAttribute('hidden', '');
    });

    // Find the parent subscription group for the selected radio
    const subscriptionGroup = radioButton.closest('subscription-group');
    
    if (subscriptionGroup) {
      // Show the frequency options for the selected subscription group
      const frequencyOptions = subscriptionGroup.querySelector('.subscriptions__frequency-options');
      if (frequencyOptions) {
        frequencyOptions.removeAttribute('hidden');
      }

      // Get the current selling plan from this group's selector
      const selector = subscriptionGroup.querySelector('[data-id="selling-plan-selector"]');
      
      if (selector) {
        const sellingPlanId = selector.value;
        
        // Update the subscription group attributes
        subscriptionGroup.setAttribute('selling-plan-id', sellingPlanId);
        
        // Extract and set the frequency amount from the selected option's data attribute
        const selectedOption = selector.querySelector(`option[value="${sellingPlanId}"]`);
        if (selectedOption) {
          const frequencyAmount = selectedOption.getAttribute('data-frequency-amount');
          if (frequencyAmount) {
            subscriptionGroup.setAttribute('data-frequency-amount', frequencyAmount);
          }
          
          // Update pricing based on the selected plan
          this.updateSubscriptionPricing(subscriptionGroup, selectedOption);
        }
        
        // Update the form's hidden input
        this.updateFormSellingPlan(sellingPlanId);
      }
    }
  }

  /**
   * Update subscription pricing based on the selected plan
   * @param {HTMLElement} subscriptionGroup - The subscription group element
   * @param {HTMLElement} selectedOption - The selected option element
   */
  updateSubscriptionPricing(subscriptionGroup, selectedOption) {

    // Step 1: Get the default variant price from the script element
    const defaultVariantPriceScript = document.getElementById(`DefaultVariantPrice-${subscriptionGroup.getAttribute('section-id')}-${subscriptionGroup.getAttribute('product-id')}`);
    if (!defaultVariantPriceScript) return;

    const defaultVariantPrice = JSON.parse(defaultVariantPriceScript.textContent)['default-variant-price'];

    // Step 2: Get the data-prepaid attribute from the selected option
    const isPrepaid = selectedOption.getAttribute('data-prepaid') === 'true';

    // Step 3 & 4: Calculate and update the price based on prepaid status
    if (isPrepaid) {
      // For prepaid: multiply by frequency amount
      const frequencyAmount = parseInt(selectedOption.getAttribute('data-frequency-amount')) || 1;
      const totalPrice = defaultVariantPrice * frequencyAmount;
      
      // Format as money and update prepaid-price element
      const prepaidPriceElement = subscriptionGroup.querySelector('span#group-price[data-ref="prepaid-price"]');
      if (prepaidPriceElement) {
        prepaidPriceElement.textContent = this.formatMoney(totalPrice);
      }
    } else {
      // For recurring: use the default variant price as-is
      const recurringPriceElement = subscriptionGroup.querySelector('span#group-price[data-ref="recurring-price"]');
      if (recurringPriceElement) {
        recurringPriceElement.textContent = this.formatMoney(defaultVariantPrice);
      }
    }
  }

  /**
   * Format a number as money using the standalone format-money module
   * @param {number} amount - The amount to format (in dollars)
   * @returns {string} Formatted money string
   */
  formatMoney(amount) {
    // Use the global FormatMoney module from format-money.js
    if (window.FormatMoney && window.FormatMoney.formatMoneyFromDollars) {
      return window.FormatMoney.formatMoneyFromDollars(amount);
    }
  }

  /**
   * Update the form's hidden selling_plan input and prepaid property
   * @param {string} sellingPlanId - The selling plan ID to set
   */
  updateFormSellingPlan(sellingPlanId) {
    if (!this.formId) {
      console.warn('No form ID specified on subscription-picker');
      return;
    }

    // Find the form element using the form attribute
    const form = document.getElementById(this.formId);
    
    if (!form) {
      console.warn(`Form with ID "${this.formId}" not found`);
      return;
    }

    // Find the hidden input with name="selling_plan"
    let sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    
    // If the input doesn't exist, create it
    if (!sellingPlanInput) {
      sellingPlanInput = document.createElement('input');
      sellingPlanInput.type = 'hidden';
      sellingPlanInput.name = 'selling_plan';
      form.appendChild(sellingPlanInput);
    }

    // Update the value
    sellingPlanInput.value = sellingPlanId;

    // Find the selected option to get prepaid status
    const selectedOption = this.querySelector(`option[value="${sellingPlanId}"]`);
    const isPrepaid = selectedOption ? selectedOption.getAttribute('data-prepaid') === 'true' : false;

    // Find or create the prepaid property input
    let prepaidInput = form.querySelector('input[name="properties[_is_prepaid]"]');
    
    if (!prepaidInput) {
      prepaidInput = document.createElement('input');
      prepaidInput.type = 'hidden';
      prepaidInput.name = 'properties[_is_prepaid]';
      form.appendChild(prepaidInput);
    }

    // Update the prepaid value - set to 'true' if prepaid, otherwise 'false'
    prepaidInput.value = isPrepaid ? 'true' : 'false';

    // Dispatch a custom event to notify other components of the change
    this.dispatchEvent(new CustomEvent('sellingPlanChanged', {
      detail: { sellingPlanId, isPrepaid },
      bubbles: true
    }));
  }
}

class SubscriptionGroup extends HTMLElement {
  connectedCallback() {
    // This element serves as a container for subscription group functionality
    // The main logic is handled by the parent SubscriptionPicker component
  }
}

// Define the custom elements
if (!window.customElements.get('subscription-picker')) {   
  window.customElements.define('subscription-picker', SubscriptionPicker);
}
if (!window.customElements.get('subscription-group')) {
  window.customElements.define('subscription-group', SubscriptionGroup);
}
